import ApiService from '@/services/apiService';
import { mapState, mapGetters } from 'vuex';
import { userItemsPerListMixin } from '@/utils/vuex';
import backgroundJobFailureMixin from '@/mixins/backgroundJobFailure';
import { localizeDate } from '@/utils/date';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import apiService from '@/services/apiService';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['createdAt'],
  sortDesc: [true],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'CoreBackgroundJobsExportTable',
  mixins: [userItemsPerListMixin, backgroundJobFailureMixin],
  props: {
    accounts: {
      type: Array,
      default: () => [],
      required: false
    },
    searchTypes: {
      type: Array,
      default: () => [],
      required: false
    },
    // if used in the device-type list, do not restore the last filter from the virtual-device-list
    // should show ALL unfiltered devices by default
    restoreFilter: {
      type: Boolean,
      required: false,
      default: () => true
    },
    containerClass: {
      type: String,
      default: () => 'n20-mainrow',
      required: false
    },
    jobType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      deletePrompt: false,
      cancelPromptCsvImport: false,
      cancelPromptBulkEdit: false,
      cancelPromptCsvExport: false,
      disableFetchOnCreatedMixin: true,
      lastSearch: null,
      searchItems: [],
      isFetching: false,
      tableOptions: {
        ...defaultTableOptions
      },
      selected: [],
      list: [],
      serverItemsLength: null,
      selectedItem: {}
    };
  },
  computed: {
    ...mapState('app', ['color', 'permissions']),
    ...mapGetters({
      sessionUserId: 'authentication/userId'
    }),
    isEmptyList() {
      return this.searchItems.length === 0 && this.serverItemsLength <= 0 && this.isFetching === false;
    },
    tableHeaders() {
      return [{
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.id'),
        value: 'id',
        align: 'center',
        sortable: false
      }, {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.name'),
        value: 'title',
        align: 'start',
        sortable: false
      }, {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.state'),
        value: 'state',
        align: 'start',
        sortable: false
      }, {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.resource'),
        value: 'type',
        align: 'start',
        sortable: false
      }, {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.createdAt'),
        value: 'createdAt'
      }, ...(this.isReadRole ? [{
        align: 'end',
        sortable: false,
        value: 'actions'
      }] : [])];
    },
    tableFooterProps() {
      return {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [defaultItemsPerPage, 20, 50]
      };
    },
    isReadRole() {
      return this.permissions['VirtualDevice.read'] || this.permissions['DigitalTwin.read'];
    },
    accountsIds() {
      return this.accounts.map(acc => acc.id);
    },
    canDeleteItem() {
      return this.permissions['all.manage'] || this.isReadRole;
    }
  },
  watch: {
    accounts: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.fetchList();
    }, 500),
    canFetch() {
      if (!this.canFetch) {
        return;
      }
      this.fetchList();
    },
    'tableOptions.page'(newVal, oldVal) {
      if (newVal !== oldVal && !this.isFetching) {
        this.fetchList();
      }
    }
  },
  async mounted() {
    await this.itemsPerPageReadyPromise;
    this.fetchList(true);
  },
  methods: {
    get,
    getJobStatus(item) {
      if (this.jobFailureReason(item)) {
        return this.$i18n_t('BackgroundJobs.BackgroundJobs.status.failed');
      }
      const status = get(item, 'state');
      if (status === 'in progress' && get(item, 'type') === 'deviceStatesCsvExport' && get(item, 'tasks.started', 0) === 0) {
        const position = get(item, 'queuePosition', null);
        if (position === null) {
          return this.$i18n_t('BackgroundJobs.BackgroundJobs.waitingForWorker');
        }
        return this.$i18n_t('BackgroundJobs.BackgroundJobs.waitingForWorkerPosition', {
          position
        });
      }
      const i18nKey = `BackgroundJobs.BackgroundJobs.status.${status.replace(' ', '')}`;
      if (this.$i18n_te(i18nKey)) {
        return this.$i18n_t(i18nKey);
      }
      return status;
    },
    getResourceType(item) {
      const type = get(item, 'executions.params.type');
      switch (type) {
        case 'virtualDevice':
          return 'virtual-device-state-values';
        case 'digitalTwin':
          return 'digital-twin-state-values';
        default:
          return 'n/a';
      }
    },
    async downloadFile(item) {
      const fileId = get(item, 'executions.result.data.fileId');
      if (!fileId) {
        return;
      }
      await apiService.downloadFile(fileId, `${item.title}.zip`, 2);
    },
    fetchList(fromStart = false) {
      if (this.isFetching) {
        return;
      }
      this.isFetching = true;
      const searchItems = (this.searchItems || []).map(item => ({
        variable: item.param,
        // support item.search.value === null, so do not simply have a >search.value || search< expression
        search: (item.search || {}).value === undefined ? item.search : item.search.value,
        key: item.key
      }));
      if (!isEqual(this.lastSearch, JSON.stringify(searchItems))) {
        this.lastSearch = JSON.stringify(searchItems);
        this.tableOptions = {
          ...this.tableOptions,
          page: 1
        };
      }
      const tableOptions = fromStart ? {
        ...this.tableOptions,
        page: 1
      } : this.tableOptions;
      let {
        sortBy,
        sortDesc,
        page,
        itemsPerPage
      } = tableOptions;
      sortBy = sortBy && sortBy.length ? sortBy[0] : 'createdAt';
      const doSortDesc = sortDesc && sortDesc.length ? sortDesc[0] : true;
      const usedPage = Math.max(1, page || 1);
      const limit = itemsPerPage || defaultItemsPerPage;
      const offset = (usedPage - 1) * limit;
      ApiService.fetchBackgroundJobByAccountIds(this.accountsIds, sortBy, doSortDesc, limit, offset, this.jobType).then(result => {
        this.list = result.data;
        if (searchItems.length) {
          this.tableOptions.itemsPerPage = 10;
        }
        this.serverItemsLength = result.pagination.itemCount;
        this.$emit('load-items', result);
      }).catch(error => {
        console.error('Error fetching virtual devices', error);
      }).finally(() => {
        this.isFetching = false;
      });
    },
    deletePromise() {
      return ApiService.deleteBackgroundRunner(this.selectedItem.id);
    },
    handleClickRunner(item) {
      this.$emit('show-item-info', item);
    },
    localizeDate,
    showDeletePrompt(item) {
      this.selectedItem = item;
      this.deletePrompt = true;
    },
    showCancelPrompt(item) {
      this.selectedItem = item;
      switch (item.type) {
        case 'virtualDevicesCsvImport':
        case 'digitalTwinBulkDuplicate':
          this.cancelPromptCsvImport = true;
          break;
        case 'virtualDevicesEdit':
          this.cancelPromptBulkEdit = true;
          break;
        case 'deviceStatesCsvExport':
          this.cancelPromptCsvExport = true;
          break;
      }
    },
    cancelRunner() {
      this.cancelPromptCsvImport = false;
      this.cancelPromptBulkEdit = false;
      this.cancelPromptCsvExport = false;
      ApiService.cancelBackgroundRunner(this.selectedItem.id).then(() => {
        this.$snack(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.cancelStarted'), 'success');
        this.fetchList();
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.cancelFailed'), 'error');
        }
      });
    },
    deleteItem() {
      this.deletePrompt = false;
      this.deletePromise().then(() => {
        this.$snack(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.deleteSuccess'), 'success');
        this.fetchList();
      }).catch(error => {
        console.error(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.deleteFailed'), error);
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.deleteFailed'), 'error');
        }
      });
    }
  }
};